import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { readViteEnv } from '../../lib/readViteEnv';

type Props = {
  lat: number;
  lng: number;
  title: string;
  subtitle?: string | null;
  height?: number;
};

export function IpLocationMap({ lat, lng, title, subtitle, height = 220 }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'missing_key' | 'error'>('idle');

  useEffect(() => {
    const apiKey = readViteEnv('VITE_GOOGLE_MAPS_API_KEY') ?? '';
    if (!apiKey) {
      setPhase('missing_key');
      return;
    }

    let cancelled = false;
    setPhase('loading');

    void (async () => {
      try {
        const mapIdRaw = readViteEnv('VITE_GOOGLE_MAPS_MAP_ID') ?? '';
        const mapId = mapIdRaw || 'DEMO_MAP_ID';
        const authPolicy = readViteEnv('VITE_GOOGLE_MAPS_AUTH_REFERRER_POLICY') ?? '';

        setOptions({
          key: apiKey,
          v: 'weekly',
          ...(authPolicy ? { authReferrerPolicy: authPolicy } : {}),
        });

        const { Map } = await importLibrary('maps');
        const { AdvancedMarkerElement } = await importLibrary('marker');

        if (cancelled || !mapRef.current) return;

        const map = new Map(mapRef.current, {
          center: { lat, lng },
          zoom: 11,
          mapTypeControl: false,
          fullscreenControl: true,
          streetViewControl: false,
          mapId,
        });

        new AdvancedMarkerElement({
          map,
          position: { lat, lng },
          title,
        });

        if (!cancelled) setPhase('ready');
      } catch {
        if (!cancelled) setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lat, lng, title]);

  if (phase === 'missing_key') {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>apps/admin/.env</code> to show IP location on the map.
          {subtitle ? ` (${subtitle})` : ''}
        </Typography>
      </Box>
    );
  }

  if (phase === 'error') {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'error.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="error.main">
          Could not load map for this IP location.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      {phase === 'loading' ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            zIndex: 1,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : null}
      <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />
    </Box>
  );
}
